import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Card,
  Checkbox,
  Empty,
  Select,
  Spin,
  Radio,
  Tooltip,
  InputNumber,
  Divider,
  Row,
  Typography
} from 'antd';
import { SearchOutlined, QuestionCircleOutlined, DollarOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { CurrencySelectorComponent } from 'src/_shared/components/currency-selector/CurrencySelectorComponent.jsx';
import getSymbolFromCurrency from 'currency-symbol-map';

const { Option } = Select;
const { Text } = Typography;

const SETTINGS = [
  // General Settings
  {
    id: 'recognitionCustomTitle',
    label: 'Custom RecHub Menu Label',
    note: 'Change the RecHub menu label',
    type: 'text',
    placeholder: 'RecHub',
    group: 'general',
    keywords: 'title rechub menu label custom name'
  },
  {
    id: 'customSendRecognitionBoxText',
    label: 'Custom Send Recognition Box Text',
    note: 'This text will appear on RecHub above the Select Recipients / Select Recognition buttons.',
    type: 'textarea',
    placeholder: 'Enter text here - If empty no message will appear.',
    group: 'general',
    keywords: 'send recognition text custom label rechub area message'
  },
  {
    id: 'requireBadgeSelection',
    label: 'Require Selection for Recognition Shout-Outs',
    note: 'If enabled, users must select a recognition to be included with a shout-out.',
    type: 'checkbox',
    group: 'general',
    keywords: 'badge selection require mandatory recognitions'
  },
  {
    id: 'disableRecHub',
    label: 'Disable RecHub',
    note: 'Disables the RecHub dashboard social messaging feed, on-the-spot recognition sharing between employees, and switches "My Recognitions" to the default home page. Employees can still submit colleagues for nomination recognitions and earn on-the-spot recognitions from managers.',
    type: 'checkbox',
    group: 'general',
    keywords: 'disable rechub feed dashboard turn off deactivate'
  },
  {
    id: 'disableNominations',
    label: 'Disable Nominations',
    note: 'Disables the Nominations feature and hides the nominations count from "My Recognitions & Rewards" page and the "Nominations" page from the admin sidebar.',
    type: 'checkbox',
    group: 'general',
    keywords: 'disable nominations nomination nominate recognitions turn off deactivate'
  },
  
  // Display Options
  {
    id: 'hideRecognitionSocialShare',
    label: 'Hide social sharing options on badge overview popups',
    type: 'checkbox',
    group: 'display',
    keywords: 'hide social sharing badge popup overview options'
  },
  {
    id: 'hideRecognitionLeaderboard',
    label: 'Hide Leaderboard',
    note: 'Hide the leaderboard on the RecHub page.',
    type: 'checkbox',
    group: 'display',
    keywords: 'hide leaderboard rechub page rankings'
  },
  {
    id: 'hideAllRecognitionsManager',
    label: 'Hide All Recognitions history from Managers',
    note: 'Hide the All Recognitions tab on the Manager\'s Send Recognition page.',
    type: 'checkbox',
    group: 'display',
    keywords: 'hide disable all recognitions show tab unhide manager page send recognition'
  },
  {
    id: 'recognitionPointsCurrency',
    label: 'Currency for Recognition Points',
    type: 'currency',
    group: 'display',
    keywords: 'currency recognition points money dollar format'
  },
  
  // Management Settings
  {
    id: 'enableManagerSendForm',
    label: 'Enable Manager Send Form',
    note: 'If enabled, managers will have access to a specialized recognition form to give out recognitions to employees.',
    type: 'checkbox',
    group: 'manager',
    keywords: 'manager send form enable special recognition'
  },
  {
    id: 'requireJustification',
    label: 'Require Justification Form for Recognitions',
	  note: 'If enabled, managers and admins must provide a justification before a recognition can be sent.',
    type: 'checkbox',
    group: 'manager',
    keywords: 'justification managerrequire recognize form reason recognition'
  },
  {
    id: 'requireAdminApproval',
    label: 'Require Admin Approval',
    note: 'If enabled, all recognitions will require admin approval before being finalized.',
    type: 'checkbox',
    group: 'manager',
    keywords: 'manager approval require recognize'
  },
  {
    id: 'notificationOnApprovalDecision',
    label: 'Managers Receive Notification when Recognition is Finalized',
    note: 'If enabled, Managers will get a web notification on the completion of an approval decision by an Admin.',
    type: 'checkbox',
    group: 'manager',
    keywords: 'manager approval denial bell notification admin recognition'
  },
  
  // Financial Settings
  {
    id: 'baseSnappyBudget',
    label: 'Budget Amount Per Employee',
    note: 'This amount determines the base amount each employee is budgeted for receiving Snappy Rewards from recognitions. 1 point is the equivalent of $1.00 USD.',
    type: 'number',
    min: 0,
    step: 10,
    group: 'financial',
    keywords: 'budget employee allocation amount money funds per person'
  },
  {
    id: 'assignSnappyRewardToRecognition',
    label: 'Assign Snappy Reward to Recognition',
    note: 'If enabled, Snappy Campaign Tiers can be assigned to Recognitions when creating or editing an on-the-spot or nominated recognition. This will automatically fill in the value of the Recognition bonus amount with the Snappy Campaign Tier value.',
    type: 'checkbox',
    group: 'financial',
    keywords: 'assign snappy reward recognition automatic link'
  },
  {
    id: 'rewardsEarnedMechanism',
    label: 'Snappy Rewards Mode',
    note: 'With "Campaign", employees earn points and receive Snappy rewards which are assigned point milestones to tiers. With "One-Time Grant", Snappy rewards are assigned a monetary value and rewardedfor each recognition.',
    type: 'radio',
    options: [
      { value: 'milestone', label: 'Campaign Tiers' },
      { value: 'recognition', label: 'One-Time Grant' }
    ],
    group: 'financial',
    keywords: 'rewards earned mechanism points money funds'
  },
  {
    id: 'nominatedRecognitionsCostPoints',
    label: 'Subtract Points for Nominated Recognitions',
    note: "By default, nominated recognitions don't count against the employee's budgeted balance. If enabled, the points value of the nominated recognition will be subtracted from the employee's balance.",
    type: 'checkbox',
    group: 'financial',
    keywords: 'nominated recognitions nomination cost points money funds'
  },
  {
    id: 'allowAdminBalanceOverrides',
    label: 'Allow Admins to Override Balances',
    note: 'If checked, admins can add additional funds to an individual employee\'s balance or send Snappy Grant assigned Recognitions to employees, without a sufficient balance.',
    type: 'checkbox',
    group: 'financial',
    keywords: 'admin override balance override points funds'
  }
];

const SETTING_GROUPS = [
  {
    id: 'general',
    title: 'General Settings',
  },
  {
    id: 'display',
    title: 'Display Options',
  },
  {
    id: 'manager',
    title: 'Management Settings',
  },
  {
    id: 'financial',
    title: 'Financial Settings',
  }
];

const fuzzySearch = (text, query) => {
  if (!query) return true;
  
  const pattern = query.split('').map(char => {
    return char.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
  }).join('.*?');
  
  const regex = new RegExp(pattern, 'i');
  return regex.test(text);
};

const settingStyles = {
  settingRow: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '16px',
    padding: '12px',
    borderRadius: '4px',
    background: '#fafafa',
    border: '1px solid #f0f0f0',
  },
  labelContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '8px',
  },
  label: {
    fontWeight: '500',
    marginRight: '8px',
    flex: '0 0 auto',
    minWidth: '180px',
    maxWidth: '250px',
  },
  tooltipIcon: {
    color: '#8c8c8c',
    fontSize: '16px',
    marginLeft: '4px',
  },
  inputContainer: {
    marginBottom: '4px',
  },
  noteText: {
    color: '#8c8c8c',
    fontSize: '13px',
    marginTop: '4px',
    marginBottom: '0',
  },
  radioLayout: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  }
};

const renderSettingField = (setting) => {
  const tooltip = (
    <Tooltip title={setting.note}>
      <QuestionCircleOutlined style={settingStyles.tooltipIcon} />
    </Tooltip>
  );

  switch (setting.type) {
    case 'checkbox':
      return (
        <div style={settingStyles.settingRow}>
          <div style={settingStyles.labelContainer}>
            <Form.Item
              name={setting.id}
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Checkbox>
                <span style={{ fontWeight: '500' }}>{setting.label}</span>
              </Checkbox>
            </Form.Item>
          </div>
          {setting.note && (
            <p style={settingStyles.noteText}>{setting.note}</p>
          )}
        </div>
      );
    
    case 'number':
      return (
        <div style={settingStyles.settingRow}>
          <div style={settingStyles.labelContainer}>
            <div style={settingStyles.label}>{setting.label}</div>
          </div>
          <div style={settingStyles.inputContainer}>
            <Form.Item
              name={setting.id}
              rules={[{ required: true, message: 'Please input the required value!' }]}
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                prefix={<DollarOutlined />}
                min={setting.min || 0}
                step={setting.step || 1}
                style={{ width: '100%', maxWidth: '200px' }}
              />
            </Form.Item>
          </div>
          {setting.note && (
            <p style={settingStyles.noteText}>{setting.note}</p>
          )}
        </div>
      );
    
    case 'radio':
      return (
        <div style={settingStyles.settingRow}>
          <div style={settingStyles.labelContainer}>
            <div style={settingStyles.label}>{setting.label}</div>
          </div>
          <div style={settingStyles.inputContainer}>
            <Form.Item
              name={setting.id}
              rules={[{ required: true, message: 'Please select an option!' }]}
              style={{ marginBottom: 0 }}
            >
              <Radio.Group style={settingStyles.radioLayout}>
                {setting.options.map(option => (
                  <Radio key={option.value} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
          </div>
          {setting.note && (
            <p style={settingStyles.noteText}>{setting.note}</p>
          )}
        </div>
      );
    
    case 'currency':
      return (
        <div style={settingStyles.settingRow}>
          <div style={settingStyles.labelContainer}>
            <div style={settingStyles.label}>{setting.label}</div>
          </div>
          <div style={settingStyles.inputContainer}>
            <Form.Item 
              name={setting.id}
              style={{ marginBottom: 0 }}
            >
              <CurrencySelectorComponent />
            </Form.Item>
          </div>
          {setting.note && (
            <p style={settingStyles.noteText}>{setting.note}</p>
          )}
        </div>
      );

    case 'textarea':
      return (
        <div style={settingStyles.settingRow}>
          <div style={settingStyles.labelContainer}>
            <div style={settingStyles.label}>{setting.label}</div>
          </div>
          <div style={settingStyles.inputContainer}>
            <Form.Item
              name={setting.id}
              rules={[{ required: false }]}
              style={{ marginBottom: 0 }}
            >
              <Input.TextArea
                placeholder={setting.placeholder || ''} 
                style={{ maxWidth: '350px' }}
              />
            </Form.Item>
          </div>
          {setting.note && (
            <p style={settingStyles.noteText}>{setting.note}</p>
          )}
        </div>
      );
    
    case 'text':
    default:
      return (
        <div style={settingStyles.settingRow}>
          <div style={settingStyles.labelContainer}>
            <div style={settingStyles.label}>{setting.label}</div>
          </div>
          <div style={settingStyles.inputContainer}>
            <Form.Item
              name={setting.id}
              rules={[{ required: false }]}
              style={{ marginBottom: 0 }}
            >
              <Input 
                placeholder={setting.placeholder || ''} 
                style={{ maxWidth: '350px' }}
              />
            </Form.Item>
          </div>
          {setting.note && (
            <p style={settingStyles.noteText}>{setting.note}</p>
          )}
        </div>
      );
  }
};

const ApiKeyStatusComponent = ({ apiKeyStatus, currentApiKey }) => {
  const getStatusConfig = () => {
    switch (apiKeyStatus) {
      case 'checking':
        return {
          icon: <LoadingOutlined style={{ color: '#1890ff' }} />,
          text: 'Checking status...',
          color: '#1890ff'
        };
      case 'active':
        return {
          icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
          text: 'Active',
          color: '#52c41a'
        };
      case 'inactive':
        return {
          icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
          text: 'Inactive',
          color: '#ff4d4f'
        };
      default:
        return {
          icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
          text: 'None',
          color: '#ff4d4f'
        };
    }
  };

  const statusConfig = getStatusConfig();

  return (
    <div style={{
      marginBottom: '24px',
      padding: '16px',
      border: '1px solid #f0f0f0',
      borderRadius: '8px',
      backgroundColor: '#fafafa'
    }}>
      <div style={{ marginBottom: '12px' }}>
        <Text strong style={{ fontSize: '16px' }}>API Key Status</Text>
      </div>
      
      <div style={{ marginBottom: '8px' }}>
        <Text strong>Current API Key: </Text>
        {currentApiKey?.maskedKey ? (
          <Text code>{currentApiKey.maskedKey}</Text>
        ) : (
          <Text type="secondary">None</Text>
        )}
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Text strong>Status: </Text>
        {statusConfig.icon}
        <Text style={{ color: statusConfig.color, fontWeight: '500' }}>
          {statusConfig.text}
        </Text>
      </div>
    </div>
  );
};

const RecognitionSettingsPresentation = ({
  campaigns,
  currentUser,
  snappyCampaigns,
  formVisibleCampaignId,
  setFormVisibleCampaignId,
  onFinish,
  onCreateCampaign,
  handleUpdateCompany,
  showCreateCampaignForm,
  setShowCreateCampaignForm,
  inlineEditingIndex,
  onInlineEditSave,
  onInlineEditCancel,
  form,
  createCampaignForm,
  inlineFormData,
  handleInlineInputChange,
  handleInlineEditClick,
  isSubmitting,
  deletingTierId,
  updatingTierId,
  onDelete,
  apiKeyStatus,
  currentApiKey,
}) => {
  const campaign = campaigns.length > 0 ? campaigns[0] : null;
  const tiers = campaign?.tiers || [];
  const oneTimeRewardMode = currentUser?.company?.recognitionSettings?.rewardsEarnedMechanism === "recognition";
  const currencyCode = currentUser?.company?.recognitionSettings?.recognitionPointsCurrency || 'USD';
  const currencySymbol = getSymbolFromCurrency(currencyCode);
  
  const [searchQuery, setSearchQuery] = useState('');
  
  const filteredSettings = SETTINGS.filter(setting => {
    const searchableText = `${setting.label} ${setting.note || ''} ${setting.keywords || ''}`;
    return fuzzySearch(searchableText, searchQuery);
  });
  
  const visibleGroups = SETTING_GROUPS.filter(group => {
    if (searchQuery === '') return true;
    return filteredSettings.some(setting => setting.group === group.id);
  });
  const showCompanySettings = searchQuery === '' || filteredSettings.length > 0;
  const showCampaignSection = tiers.length > 0;

  const getInitialValues = () => {
    const initialValues = currentUser?.company?.recognitionSettings || {};
    return {
      ...initialValues,
      recognitionPointsCurrency: initialValues?.recognitionPointsCurrency || 'USD', 
      rewardsEarnedMechanism: initialValues?.rewardsEarnedMechanism || 'milestone',
      baseSnappyBudge: initialValues?.baseSnappyBudge ?? 0,
    };
  };

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search settings..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ width: '100%', maxWidth: '500px' }}
          allowClear
          size="large"
        />
        {searchQuery && (
          <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
            {tiers.length === 0 ? (
              <span>No matching settings found</span>
            ) : (
              <span>
                Found {filteredSettings.length} matching settings
              </span>
            )}
          </div>
        )}
      </div>
      
      {showCompanySettings && (
        <>
          <div className="page-title">
            <h2 className="page-heading-manage-reward">Recognition Settings</h2>
          </div>
          <div className="setting-card">
            <h4 className="setting-card-title">Recognition Customizations</h4>
            <Form
              layout="vertical"
              onFinish={handleUpdateCompany}
              initialValues={getInitialValues()}
            >
              {searchQuery ? (
                <div style={{ maxWidth: '800px' }}>
                  {filteredSettings.map(setting => (
                    <div key={setting.id}>
                      {renderSettingField(setting)}
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {visibleGroups.map(group => (
                    <div key={group.id} style={{ marginBottom: '32px' }}>
                      <Divider orientationMargin={10} orientation="left">
                        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>{group.title}</span>
                      </Divider>
                      <div style={{ maxWidth: '800px' }}>
                        {SETTINGS.filter(s => s.group === group.id).map(setting => (
                          <div key={setting.id}>
                            {renderSettingField(setting)}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
              
              {(searchQuery === '' || filteredSettings.length > 0) && (
                <Form.Item style={{ marginTop: '32px' }}>
                  <Button 
                    type="primary" 
                    size="large" 
                    htmlType="submit"
                    style={{
                      borderRadius: '20px',
                      padding: '0 24px',
                      height: '42px',
                      fontWeight: '500'
                    }}
                  >
                    Save Settings
                  </Button>
                </Form.Item>
              )}
            </Form>
          </div>
        </>
      )}
      
      {true && (
        <>
          <div className="page-title">
            <h2 className="page-heading-manage-reward">Snappy Configuration</h2>
          </div>
          
          <ApiKeyStatusComponent
            apiKeyStatus={apiKeyStatus}
            currentApiKey={currentApiKey}
          />
          {!campaign && true && (
            <Card>
              <Empty description="No campaign found, would you like to create one?" />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '16px',
                }}
              >
                <Button
                  type="primary"
                  onClick={() => setShowCreateCampaignForm(true)}
                  style={{ width: '200px' }}
                >
                  Create Campaign
                </Button>
              </div>
            </Card>
          )}

          {showCreateCampaignForm && (
            <Card title="Create Campaign" style={{ marginTop: '16px' }}>
              <Form
                form={createCampaignForm}
                layout="vertical"
                onFinish={onCreateCampaign}
              >
                <Form.Item
                  name="campaignName"
                  label="Campaign Name"
                  rules={[
                    { required: true, message: 'Please input the campaign name!' },
                  ]}
                >
                  <Input placeholder="Enter campaign name" />
                </Form.Item>

                <Form.Item
                  name="snappyAPIKey"
                  label="Snappy API Key"
                  rules={[
                    {
                      required: true,
                      message: 'Please input the Snappy API Key!',
                    },
                  ]}
                >
                  <Input placeholder="Enter Snappy API Key" />
                </Form.Item>

                <Form.Item>
                  <Button type="primary" htmlType="submit">
                    Create
                  </Button>
                  <Button
                    style={{ marginLeft: '8px' }}
                    onClick={() => setShowCreateCampaignForm(false)}
                  >
                    Cancel
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          )}

          {campaign && (
            <div>
              {tiers.length > 0 ? (
                tiers.map((tier) => (
                  <Card key={tier.id} style={{ marginBottom: '16px' }}>
                    {deletingTierId === tier.id ? (
                      <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin size="large" />
                      </div>
                    ) : updatingTierId === tier.id ? (
                      <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin size="large" />
                      </div>
                    ) : inlineEditingIndex === tier.id ? (
                      <div>
                        <p>
                          <strong>{oneTimeRewardMode ? "One-Time Snappy Grant Name" : "Tier Name"}:</strong>{' '}
                          <Input
                            value={inlineFormData.tierName}
                            onChange={(e) =>
                              handleInlineInputChange('tierName', e.target.value)
                            }
                            placeholder={oneTimeRewardMode ? "Enter the One-Time Snappy Grant name" : "Enter the tier name"}
                          />
                        </p>
                        <p>
                          <strong>{oneTimeRewardMode ? "One-Time Snappy Grant Description" : "Tier Description"}:</strong>{' '}
                          <Input
                            value={inlineFormData.tierDescription}
                            onChange={(e) =>
                              handleInlineInputChange(
                                'tierDescription',
                                e.target.value
                              )
                            }
                            placeholder={oneTimeRewardMode ? "Enter the One-Time Snappy Grant description" : "Enter the tier description"}
                          />
                        </p>
                        <p>
                          <strong>{oneTimeRewardMode ? "Snappy One-Time Grant Cost" : "Points Required to Unlock"}:</strong>{' '}
                          <InputNumber
                            value={inlineFormData.pointsRequired}
                            onChange={(value) =>
                              handleInlineInputChange('pointsRequired', value)
                            }
                            prefix={oneTimeRewardMode ? currencySymbol || "$" : null}
                            style={{ width: '100%' }}
                          />
                        </p>
                        <p>
                          <strong>{"Snappy Campaign"}:</strong>{' '}
                          <Select
                            value={inlineFormData.snappyCampaignId}
                            onChange={(value) =>
                              handleInlineInputChange('snappyCampaignId', value)
                            }
                            style={{ width: '100%' }}
                            placeholder="Select a Snappy Campaign"
                            showSearch
                            filterOption={(input, option) =>
                              option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                          >
                            {snappyCampaigns.map(campaign => (
                              <Option key={campaign.id} value={campaign.id}>
                                {campaign.name} ({campaign.id})
                              </Option>
                            ))}
                          </Select>
                        </p>
                        <Button
                          type="primary"
                          onClick={() =>
                            onInlineEditSave(tier.id, inlineFormData, campaign.id)
                          }
                          style={{ marginRight: '8px' }}
                        >
                          Save
                        </Button>
                        <Button onClick={onInlineEditCancel}>Cancel</Button>
                      </div>
                    ) : (
                      <div>
                        <p>
                          <strong>{oneTimeRewardMode ? "One-Time Snappy Grant Name" : "Tier Name"}:</strong> {tier.tierName}
                        </p>
                        <p>
                          <strong>{oneTimeRewardMode ? "One-Time Snappy Grant Description" : "Tier Description"}:</strong> {tier.tierDescription}
                        </p>
                        <p>
                          <strong>{oneTimeRewardMode ? "One-Time Snappy Grant Cost" : "Points Required to Unlock"}:</strong> {oneTimeRewardMode ? currencySymbol : ""}{tier.pointsRequired}
                        </p>
                        <p>
                          <strong>{oneTimeRewardMode ? "Snappy External Campaign ID" : "Snappy External Campaign ID"}:</strong> {tier.snappyCampaignId}
                        </p>
                        <Button
                          type="link"
                          onClick={() => handleInlineEditClick(tier.id, tier)}
                          style={{ marginRight: '8px' }}
                        >
                          Edit
                        </Button>
                        <Button
                          type="link"
                          danger
                          onClick={() => onDelete(tier.id, campaign.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    )}
                  </Card>
                ))
              ) : (
                <div>
                  <Empty description={oneTimeRewardMode ? "No Snappy Grants found, would you like to create one?" : "No tiers found, would you like to create one?"} />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginTop: '16px',
                    }}
                  >
                    <Button
                      type="primary"
                      onClick={() =>
                        setFormVisibleCampaignId(
                          formVisibleCampaignId === campaign.id ? null : campaign.id
                        )
                      }
                      style={{ width: '200px' }}
                    >
                      {oneTimeRewardMode ? "Create Snappy Grant" : "Create Tier"}
                    </Button>
                  </div>
                </div>
              )}

              {tiers.length > 0 && !searchQuery && (
                <Button
                  type="primary"
                  onClick={() =>
                    setFormVisibleCampaignId(
                      formVisibleCampaignId === campaign.id ? null : campaign.id
                    )
                  }
                  style={{ marginTop: '16px' }}
                >
                  {oneTimeRewardMode ? "Add Another Snappy Grant" : "Add Another Tier"}
                </Button>
              )}

              {formVisibleCampaignId === campaign.id && !searchQuery && (
                <Card title={oneTimeRewardMode ? "Create Snappy Grant" : "Create Tier"} style={{ marginTop: '16px' }}>
                  {isSubmitting ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Spin size="large" />
                    </div>
                  ) : (
                    <Form
                      form={form}
                      layout="vertical"
                      onFinish={(values) => {
                        onFinish(values, campaign.id);
                        form.resetFields();
                      }}
                    >
                      <Form.Item
                        name="tierName"
                        label={oneTimeRewardMode ? "Reward Name" : "Tier Name"}
                        rules={[
                          {
                            required: true,
                            message: oneTimeRewardMode ? 'Please input the reward name!' : 'Please input the tier name!',
                          },
                        ]}
                      >
                        <Input placeholder={oneTimeRewardMode ? "Enter the reward name" : "Enter the tier name"} />
                      </Form.Item>

                      <Form.Item
                        name="tierDescription"
                        label={oneTimeRewardMode ? "Reward Description" : "Tier Description"}
                        rules={[
                          {
                            required: true,
                            message: oneTimeRewardMode ? 'Please input the reward description!' : 'Please input the tier description!',
                          },
                        ]}
                      >
                        <Input placeholder={oneTimeRewardMode ? "Enter the reward description" : "Enter the tier description"} />
                      </Form.Item>

                      <Form.Item
                        name="pointsRequired"
                        label={oneTimeRewardMode ? "Reward Cost" : "Points Required to Unlock"}
                        rules={[
                          {
                            required: true,
                            message: oneTimeRewardMode ? 'Please input the required points!' : 'Please input the required points!',
                          },
                        ]}
                      >
                        <InputNumber
                          placeholder={oneTimeRewardMode ? "Enter the reward cost" : "Enter points required"}
                          style={{ width: '100%' }}
                          prefix={oneTimeRewardMode ? currencySymbol : null}
                        />
                      </Form.Item>

                      <Form.Item
                        name="snappyCampaignId"
                        label="Snappy Campaign"
                        rules={[
                          {
                            required: true,
                            message: 'Please select a Snappy Campaign!',
                          },
                        ]}
                      >
                        <Select
                          placeholder="Select a Snappy Campaign"
                          style={{ width: '100%' }}
                          showSearch
                          filterOption={(input, option) =>
                            option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                        >
                          {snappyCampaigns.map(campaign => (
                            <Option key={campaign.id} value={campaign.id}>
                              {campaign.name} ({campaign.id})
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item>
                        <Button type="primary" htmlType="submit">
                          Submit
                        </Button>
                        <Button
                          style={{ marginLeft: '8px' }}
                          onClick={() => setFormVisibleCampaignId(null)}
                        >
                          Cancel
                        </Button>
                      </Form.Item>
                    </Form>
                  )}
                </Card>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RecognitionSettingsPresentation;
